import React from 'react';
import { motion } from 'framer-motion';
import { Clock, PauseCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatRushCountdown } from '../services/rushStatusApi';

interface RushBlockScreenProps {
  /** Message personnalisé du restaurateur (renvoyé par l'API) */
  message: string;
  /** Secondes restantes (décrémentées par le hook) */
  remainingSeconds: number;
  /** true = fermeture programmée (congés) → message seul, pas de compte à rebours */
  isScheduled?: boolean;
  /** Date de réouverture formatée (congés), affichée sous le message si dispo */
  reopenDate?: string | null;
  /** Nom du restaurant, affiché en en-tête si dispo */
  storeName?: string;
  /** Logo du restaurant, affiché en en-tête si dispo */
  logoUrl?: string;
}

/**
 * v5 — Écran de blocage "Mode Rush".
 *
 * Plein écran, sobre, lisible de loin (kiosk).
 * Le compte à rebours est l'information principale → très gros.
 * Les couleurs viennent des variables CSS du thème du store.
 */
export const RushBlockScreen: React.FC<RushBlockScreenProps> = ({
  message,
  remainingSeconds,
  isScheduled = false,
  reopenDate = null,
  storeName,
  logoUrl,
}) => {
  const { t } = useTranslation();
  const countdown = formatRushCountdown(remainingSeconds);
  const isFinishing = remainingSeconds > 0 && remainingSeconds <= 10;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-8 py-10 overflow-y-auto"
      style={{ backgroundColor: 'var(--color-background, #FFFFFF)' }}
      role="status"
      aria-live="polite"
    >
      {/* En-tête store (optionnel) */}
      {(logoUrl || storeName) && (
        <div className="flex flex-col items-center gap-3 mb-8">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={storeName || ''}
              className="h-20 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          {storeName && (
            <p
              className="text-xl font-semibold tracking-wide"
              style={{ color: 'var(--color-text-secondary, #6B7280)' }}
            >
              {storeName}
            </p>
          )}
        </div>
      )}

      {/* Icône pause animée */}
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="mb-8"
      >
        <div
          className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
          style={{
            background:
              'linear-gradient(135deg, var(--color-primary, #F97316), var(--color-secondary, #FB923C))',
          }}
        >
          <PauseCircle className="text-white" size={72} strokeWidth={2} />
        </div>
      </motion.div>

      {/* Message du restaurateur */}
      <motion.div
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="text-center max-w-3xl mb-10"
      >
        <h1
          className="text-4xl font-bold mb-4"
          style={{ color: 'var(--color-text, #111827)' }}
        >
          {isScheduled
            ? t('Nous sommes actuellement fermés')
            : t('Commandes momentanément suspendues')}
        </h1>
        {message && (
          <p
            className="text-2xl leading-relaxed"
            style={{ color: 'var(--color-text-secondary, #6B7280)' }}
          >
            {message}
          </p>
        )}
        {/* Date de réouverture (congés) — affichée seulement si le backend l'a fournie
            et qu'elle est exploitable (parseUntilDate a validé la vraisemblance). */}
        {isScheduled && reopenDate && (
          <div
            className="mt-6 inline-flex items-center gap-2.5 px-5 py-3 rounded-2xl text-xl font-semibold"
            style={{
              backgroundColor: 'var(--color-primary, #F97316)',
              color: '#FFFFFF',
            }}
          >
            <Clock size={22} />
            <span>{t('Réouverture {{date}}', { date: reopenDate })}</span>
          </div>
        )}
      </motion.div>

      {/* Compte à rebours — uniquement pour un rush court, pas une fermeture congés */}
      {!isScheduled && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="flex flex-col items-center"
        >
          <div
            className="flex items-center gap-3 mb-3 text-lg font-semibold uppercase tracking-widest"
            style={{ color: 'var(--color-text-secondary, #6B7280)' }}
          >
            <Clock size={22} />
            <span>{t('Réouverture dans')}</span>
          </div>

          <div
            className={`font-black tabular-nums leading-none ${
              isFinishing ? 'animate-pulse' : ''
            }`}
            style={{
              fontSize: 'clamp(5rem, 18vw, 14rem)',
              color: 'var(--color-primary, #F97316)',
              letterSpacing: '-0.02em',
            }}
          >
            {countdown}
          </div>
        </motion.div>
      )}

      {/* Sous-texte : "reprise auto" pour un rush, message serveur pour une fermeture */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="mt-10 text-xl text-center"
        style={{ color: 'var(--color-text-secondary, #6B7280)' }}
      >
        {isScheduled
          ? t('Merci de votre compréhension')
          : t('Les commandes reprendront automatiquement')}
      </motion.p>

      {/* Barre de progression : seulement pour un rush dans sa dernière minute */}
      {!isScheduled && remainingSeconds > 0 && remainingSeconds <= 60 && (
        <div
          className="mt-8 w-full max-w-md h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: 'var(--color-border, #E5E7EB)' }}
        >
          <div
            className="h-full rounded-full transition-all duration-1000 ease-linear"
            style={{
              width: `${(remainingSeconds / 60) * 100}%`,
              backgroundColor: 'var(--color-primary, #F97316)',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RushBlockScreen;
